import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import CountdownTimer from './CountdownTimer';
import OtpInput from './OtpInput';
import {
	dividerStyles,
	errorMessageStyles,
	formGroupStyles,
	getLinkButtonStyles,
	getSubmitButtonStyles,
	iconContainerStyles,
	loadingSpinnerStyles,
	maskedPhoneTextStyles,
	modalBodyStyles,
	modalHeaderStyles,
	modalSubtitleStyles,
	modalTitleStyles,
	resendActionsStyles,
	resendContainerStyles,
	resendTextStyles,
	stepTransitionLeftStyles,
} from './styled';
import type { OtpVerificationStepProps } from './types';

const OTP_LENGTH = 6;

const OtpVerificationStep: FC<OtpVerificationStepProps> = ({
	phoneNumber,
	deliveryMethod,
	onVerify,
	onResend,
	onChangePhone,
	isLoading,
	error,
	countdown,
	resendCooldown,
	isExpired,
}) => {
	const [otp, setOtp] = useState('');

	const maskPhoneNumber = (phone: string): string => {
		const normalizedPhone = phone.startsWith('0') ? phone : `0${phone}`;
		if (normalizedPhone.length < 4) return normalizedPhone;
		const lastFour = normalizedPhone.slice(-2);
		return `+234 **** **${lastFour}`;
	};

	const handleOtpChange = useCallback((value: string) => {
		setOtp(value);
	}, []);

	const handleVerify = useCallback(() => {
		if (otp.length === OTP_LENGTH && !isLoading && !isExpired) {
			onVerify(otp);
		}
	}, [otp, isLoading, isExpired, onVerify]);

	useEffect(() => {
		if (otp.length === OTP_LENGTH && !isLoading && !isExpired) {
			handleVerify();
		}
	}, [otp, handleVerify, isLoading, isExpired]);

	useEffect(() => {
		if (error) {
			setOtp('');
		}
	}, [error]);

	const canResend = resendCooldown === 0;
	const deliveryText = deliveryMethod === 'sms' ? 'SMS' : 'WhatsApp';
	const isVerifyDisabled = otp.length < OTP_LENGTH || isLoading || isExpired;

	return (
		<div style={stepTransitionLeftStyles}>
			<div style={modalHeaderStyles}>
				<div
					style={iconContainerStyles}
					aria-hidden='true'
				>
					<i className='fas fa-shield-alt' />
				</div>
				<h2 style={modalTitleStyles}>Enter Verification Code</h2>
				<p style={modalSubtitleStyles}>
					Sent to <span style={maskedPhoneTextStyles}>{maskPhoneNumber(phoneNumber)}</span> via {deliveryText}
				</p>
			</div>

			<div style={modalBodyStyles}>
				{error && (
					<div
						style={errorMessageStyles}
						role='alert'
					>
						<i
							className='fas fa-exclamation-triangle'
							aria-hidden='true'
						/>
						{error}
					</div>
				)}

				<div style={formGroupStyles}>
					<OtpInput
						value={otp}
						onChange={handleOtpChange}
						disabled={isLoading || isExpired}
						hasError={!!error}
					/>
				</div>

				<CountdownTimer
					seconds={countdown}
					isExpired={isExpired}
				/>

				<button
					type='button'
					onClick={handleVerify}
					disabled={isVerifyDisabled}
					style={getSubmitButtonStyles(isVerifyDisabled)}
				>
					{isLoading ? (
						<>
							<span style={loadingSpinnerStyles} />
							Verifying...
						</>
					) : (
						<>
							<i
								className='fas fa-check-circle'
								aria-hidden='true'
							/>
							Verify Code
						</>
					)}
				</button>

				<div style={resendContainerStyles}>
					<p style={resendTextStyles}>Didn&apos;t receive the code?</p>
					<div style={resendActionsStyles}>
						<button
							type='button'
							onClick={onResend}
							disabled={!canResend || isLoading}
							style={getLinkButtonStyles(!canResend || isLoading)}
						>
							{resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
						</button>
						<span style={dividerStyles}>|</span>
						<button
							type='button'
							onClick={onChangePhone}
							disabled={isLoading}
							style={getLinkButtonStyles(isLoading)}
						>
							Change Phone
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default OtpVerificationStep;
